import React, { useState, useEffect, useMemo } from 'react';
import { 
  FaFilter, FaFileExcel, FaProjectDiagram, 
  FaBolt, FaBoxOpen, FaCheckCircle, FaExclamationTriangle, 
  FaChevronDown, FaUserTie, FaRedo, FaSearch, FaFileAlt
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';

const API_URL = '/api';

// --- START: SEARCHABLE DROPDOWN COMPONENT ---
const SearchableDropdown = ({
  options = [],
  value,
  onChange,
  placeholder = "Select",
  error = false,
  width = "w-full",
  getOptionLabel = (option) => option.name || option.label || option.toString(),
  getOptionValue = (option) => option.value || option.id || option.toString(),
  getOptionSecondaryLabel = (option) => option.code || option.id || "",
  disabled = false,
  className = "",
  isLoading = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = React.useRef(null);
  const triggerRef = React.useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const dropdownListRef = React.useRef(null);
  const searchInputRef = React.useRef(null);

  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleUpdatePosition = () => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        setPosition({ top: rect.bottom + 4, left: rect.left, width: rect.width });
      }
    };
    window.addEventListener('resize', handleUpdatePosition);
    window.addEventListener('scroll', handleUpdatePosition, true); 
    return () => { 
      window.removeEventListener('resize', handleUpdatePosition); 
      window.removeEventListener('scroll', handleUpdatePosition, true); 
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && dropdownListRef.current && highlightedIndex >= 0) {
      const items = dropdownListRef.current.querySelectorAll('.dropdown-item');
      if (items[highlightedIndex]) items[highlightedIndex].scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [highlightedIndex, isOpen]);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setSearchTerm('');
      setHighlightedIndex(-1);
      setTimeout(() => { searchInputRef.current.focus(); }, 50); 
    }
  }, [isOpen]);

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    const term = searchTerm.toLowerCase();
    return options.filter(option => {
      const label = (getOptionLabel(option) || '').toString().toLowerCase();
      const secondaryLabel = (getOptionSecondaryLabel(option) || '').toString().toLowerCase();
      return label.includes(term) || secondaryLabel.includes(term);
    });
  }, [options, searchTerm, getOptionLabel, getOptionSecondaryLabel]);

  const handleKeyDown = (e) => {
    if (!isOpen) { if (e.key === 'ArrowDown' || e.key === 'Enter') { setIsOpen(true); return; } return; }
    switch (e.key) {
      case 'ArrowDown': e.preventDefault(); setHighlightedIndex(prev => { if (prev < filteredOptions.length - 1) return prev + 1; else if (filteredOptions.length > 0) return 0; return prev; }); break;
      case 'ArrowUp': e.preventDefault(); setHighlightedIndex(prev => { if (prev > 0) return prev - 1; else if (filteredOptions.length > 0) return filteredOptions.length - 1; return prev; }); break;
      case 'Enter': e.preventDefault(); if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) handleSelectOption(filteredOptions[highlightedIndex]); break;
      case 'Escape': setIsOpen(false); break; default: break;
    }
  };

  const handleSelectOption = (option) => { onChange(getOptionValue(option)); setIsOpen(false); setSearchTerm(''); setHighlightedIndex(-1); };
  
  useEffect(() => { 
    const handleClickOutside = (event) => { 
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) setIsOpen(false); 
    }; 
    document.addEventListener('mousedown', handleClickOutside); 
    return () => { document.removeEventListener('mousedown', handleClickOutside); }; 
  }, []);
  
  const selectedOption = options.find(option => getOptionValue(option) === value);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div
        className={`${width} border rounded-lg px-3 py-2.5 cursor-pointer flex justify-between items-center bg-white transition-all duration-200 ${isOpen ? 'ring-2 ring-[#6A89A7] border-[#6A89A7] shadow-md' : 'hover:border-[#6A89A7] hover:shadow-sm'} ${disabled ? 'bg-gray-100 cursor-not-allowed opacity-70' : ''}`}
        style={{ borderColor: error ? 'rgb(239, 68, 68)' : isOpen ? '#6A89A7' : 'rgb(226, 232, 240)' }}
        onClick={() => !disabled && !isLoading && setIsOpen(!isOpen)}
        ref={triggerRef}
        onKeyDown={handleKeyDown}
        tabIndex={0}
      >
        <span className="truncate text-sm text-[#384959] font-medium">
          {isLoading && <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full mr-2 inline-block"></div>}
          {selectedOption ? getOptionLabel(selectedOption) : <span className="text-gray-400 font-normal">{placeholder}</span>}
        </span>
        <FaChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </div>

      {isOpen && (
        <div
          className="fixed bg-white border border-[#E2E8F0] rounded-lg shadow-2xl flex flex-col overflow-hidden"
          style={{ top: `${position.top}px`, left: `${position.left}px`, width: `${position.width}px`, zIndex: 10000 }}
        >
          <div className="p-2 border-b border-[#E2E8F0] bg-[#F8FAFC]">
            <input ref={searchInputRef} type="text" placeholder="Search..." className="w-full px-3 py-1.5 border border-[#E2E8F0] rounded focus:outline-none focus:ring-1 focus:ring-[#6A89A7] text-sm bg-white" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} onKeyDown={handleKeyDown} />
          </div>
          <div className="overflow-y-auto custom-scrollbar max-h-60" ref={dropdownListRef}>
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <div
                  key={getOptionValue(option)}
                  className={`dropdown-item px-3 py-2 cursor-pointer border-b border-gray-50 last:border-0 transition-colors duration-150 ${highlightedIndex === index ? 'bg-[#BDDDFC] text-[#384959]' : 'hover:bg-[#F1F5F9]'} ${value === getOptionValue(option) ? 'font-bold text-[#6A89A7]' : ''}`}
                  onClick={() => handleSelectOption(option)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  <div className="text-sm truncate">{getOptionLabel(option)}</div>
                  {getOptionSecondaryLabel(option) && <div className="text-xs text-gray-500 truncate">{getOptionSecondaryLabel(option)}</div>}
                </div>
              ))
            ) : (
              <div className="px-3 py-6 text-center text-sm text-gray-500">No options found</div>
            )}
          </div>
        </div>
      )}
      {error && <span className="text-xs text-red-500 mt-1 block">{error}</span>}
    </div>
  );
};
// --- END: SEARCHABLE DROPDOWN COMPONENT ---


const ComparisonReport = () => {
  const [projects, setProjects] = useState([]);
  const [feeders, setFeeders] = useState([]);
  const [vendors, setVendors] = useState([]); 
  
  // Selection State
  const [selectedProjectId, setSelectedProjectId] = useState(''); 
  const [selectedFeederId, setSelectedFeederId] = useState(''); 
  const [selectedVendorCode, setSelectedVendorCode] = useState(''); 
  const [selectedActivityId, setSelectedActivityId] = useState('');
  const [selectedSubActivityId, setSelectedSubActivityId] = useState('');

  // Data State
  const [reportData, setReportData] = useState([]);
  const [tableSearchTerm, setTableSearchTerm] = useState('');
  
  // UI State
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(false);
  const [feedersLoading, setFeedersLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [isSummaryMode, setIsSummaryMode] = useState(false); 
  const navigate = useNavigate();

  const colors = {
    primary: '#384959', secondary: '#64748b', muted: '#A0AEC0', accent: '#6A89A7', 
    border: '#E2E8F0', tabBorder: '#BDDDFC', background: '#F9FAFB', pageBackground: '#FFFFFF'
  };

  const pageTheme = useMemo(() => ({
    bg: '#F1F5F9', sidebarBg: '#ffffff', accent: colors.accent, border: colors.border,
    text: colors.primary, muted: colors.secondary, iconColor: colors.primary,
    hoverBg: '#eff6ff', logout: colors.primary
  }), [colors]);

  const contextInfo = useMemo(() => {
    const project = projects.find(p => p.ProjectHeaderId == selectedProjectId);
    const feeder = feeders.find(f => f.FeederId == selectedFeederId);
    const vendor = vendors.find(v => v.vendor_code == selectedVendorCode);
    return {
      project: project ? project.No : 'All Projects',
      projectDesc: project ? project.Description : '',
      feeder: feeder ? feeder.Code : 'All Feeders',
      vendor: vendor ? vendor.vendor_name : 'All Vendors'
    };
  }, [selectedProjectId, selectedFeederId, selectedVendorCode, projects, feeders, vendors]);

  // --- DERIVE ACTIVITY & SUB-ACTIVITY OPTIONS ---
   const activityOptions = useMemo(() => {
    const map = new Map();
    (reportData || []).forEach(p => (p?.activities || []).forEach(a => {
      if (!map.has(a.ActivityId)) map.set(a.ActivityId, a);
    }));
    return Array.from(map.values());
  }, [reportData]);

    const subActivityOptions = useMemo(() => {
    const map = new Map();
    (reportData || []).forEach(p => (p?.activities || []).forEach(a => {
      if (!selectedActivityId || a.ActivityId == selectedActivityId) {
        (a?.subActivities || []).forEach(sa => {
          if (!map.has(sa.SubActivityId)) map.set(sa.SubActivityId, sa);
        });
      }
    }));
    return Array.from(map.values());
  }, [reportData, selectedActivityId]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const themeVars = {
      '--sidebar-line-color': pageTheme.border, '--sidebar-bg': pageTheme.sidebarBg,
      '--sidebar-text': pageTheme.text, '--accent-color': pageTheme.accent,
      '--page-border-color': pageTheme.border, '--page-bg': pageTheme.bg,
      '--text-primary': pageTheme.text, '--text-muted': pageTheme.muted,
      '--icon-color': pageTheme.iconColor, '--logout-bg': pageTheme.logout
    };
    Object.entries(themeVars).forEach(([k, v]) => document.documentElement.style.setProperty(k, v));
    const styleId = 'comparison-sidebar-overrides';
    const existing = document.getElementById(styleId);
    if (existing) existing.remove();
    const style = document.createElement('style');
    style.id = styleId;
    style.innerHTML = `
      .sidebar, .app-sidebar { background-color: var(--sidebar-bg) !important; color: var(--sidebar-text) !important; }
      .sidebar a, .sidebar .menu-item, .app-sidebar a, .app-sidebar .menu-item { color: var(--sidebar-text) !important; background-color: #ffffff !important; }
      .sidebar a.active, .app-sidebar a.active { background-color: var(--accent-color) !important; color: #ffffff !important; font-weight: 700; }
      .custom-scrollbar::-webkit-scrollbar { width: 6px; }
      .custom-scrollbar::-webkit-scrollbar-track { background: #f1f1f1; }
      .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }
    `;
    document.head.appendChild(style);
    return () => { const el = document.getElementById(styleId); if (el) el.remove(); };
  }, [pageTheme]);

  useEffect(() => { fetchProjects(); fetchVendors(); }, []);

  useEffect(() => {
    if (selectedProjectId) {
      const project = projects.find(p => p.ProjectHeaderId == selectedProjectId);
      if (project) fetchFeedersForProject(project.No);
      else { setFeeders([]); setSelectedFeederId(''); }
    } else {
      setFeeders([]); setSelectedFeederId('');
    }
  }, [selectedProjectId, projects]);

  const fetchProjects = async () => {
    try {
      setLoading(true); setError(null);
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/project-filter`, { headers: token ? { 'Authorization': `Bearer ${token}` } : {} });
      const data = await response.json();
      if (response.ok) setProjects(data); else setError(data.message || 'Failed to fetch projects');
    } catch (err) { setError('Failed to fetch projects'); } finally { setLoading(false); }
  };

  const fetchVendors = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/vendors`, { headers: token ? { 'Authorization': `Bearer ${token}` } : {} });
      const data = await response.json();
      if (response.ok) setVendors(Array.isArray(data) ? data : []);
    } catch (err) { console.error("Error fetching vendors:", err); }
  };

  const fetchFeedersForProject = async (projectNo) => {
    try {
      setFeedersLoading(true); setSelectedFeederId('');
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/project/${projectNo}/feeders-filter`, { headers: token ? { 'Authorization': `Bearer ${token}` } : {} });
      const data = await response.json();
      if (response.ok) setFeeders(data);
    } catch (err) { console.error("Error fetching feeders:", err); } finally { setFeedersLoading(false); }
  };

   const formatQty = (num) => {
    if (num === null || num === undefined || isNaN(num)) return '0';
    
    // 1. Round to 5 decimal places
    const fixed = Number(num).toFixed(5);
    
    // 2. Convert back to number to remove trailing zeros (e.g., "10.10000" -> 10.1)
    // 3. Convert back to string for rendering
    return parseFloat(fixed).toString();
  };

  const handleGenerateReport = async () => {
    if (!selectedProjectId) { setError("Please select a project."); return; }
    try {
      setFetchingData(true); setError(null); setReportData([]); setTableSearchTerm('');
      setSelectedActivityId(''); setSelectedSubActivityId(''); 
      setIsSummaryMode(false); 

      const token = localStorage.getItem('authToken');
      const params = new URLSearchParams({ projectId: selectedProjectId });
      if (selectedFeederId) params.append('feederId', selectedFeederId);
      if (selectedVendorCode) params.append('vendorCode', selectedVendorCode);

      const response = await fetch(`${API_URL}/comparison-report?${params.toString()}`, { headers: token ? { 'Authorization': `Bearer ${token}` } : {} });
      const data = await response.json();
      if (response.ok) setReportData(data); else setError(data.message || 'Failed to generate comparison report');
    } catch (err) { setError('Error generating comparison report'); } finally { setFetchingData(false); }
  };

  // --- FIX: SUMMARY REPORT CALCULATED ON FRONTEND ---
  const handleSummaryReport = async () => {
  if (!selectedProjectId) {
    setError("Please select a project.");
    return;
  }

  try {
    setFetchingData(true);
    setError(null);
    setReportData([]); // Clear previous data
    setSelectedActivityId('');
    setSelectedSubActivityId('');
    setIsSummaryMode(true); // Switch mode to Summary

    const token = localStorage.getItem('authToken');
    const params = new URLSearchParams({ projectId: selectedProjectId });
    
    if (selectedFeederId) params.append('feederId', selectedFeederId);
    if (selectedVendorCode) params.append('vendorCode', selectedVendorCode);

    // --- CHANGE THIS URL IF YOUR ENDPOINT IS DIFFERENT ---
    const response = await fetch(`${API_URL}/comparison-summary-report?${params.toString()}`, {
      headers: token ? { 'Authorization': `Bearer ${token}` } : {}
    });

    const data = await response.json();

    if (response.ok) {
      setReportData(data); // Set the exact data from API
    } else {
      setError(data.message || 'Failed to generate summary report');
    }
  } catch (err) {
    setError('Error generating summary report');
  } finally {
    setFetchingData(false);
  }
};

  const handleResetFilters = () => {
    setSelectedProjectId(''); setSelectedFeederId(''); setSelectedVendorCode('');
    setSelectedActivityId(''); setSelectedSubActivityId('');
    setReportData([]); setTableSearchTerm(''); setError(null);
    setIsSummaryMode(false);
  };

  // --- FIX: FLATTEN AND AGGREGATE DATA ---
  const flattenedReportData = useMemo(() => {
    if (!Array.isArray(reportData) || reportData.length === 0) return [];
    
    // --- NEW CODE FOR DIRECT API DATA ---
if (isSummaryMode) {
  // We simply return the data exactly as it comes from the API.
  // We calculate 'StockBalance' and 'ProjectPendingQty' if the API doesn't send them.
  return (reportData || []).map(row => ({
    ProductCode: row.ProductCode,
    ProductDescription: row.ProductDescription,
    UnitOfMeasureCode: row.UnitOfMeasureCode,
    SurveyQty: row.SurveyQty || 0,
    IndentQty: row.IndentQty || 0,
    IssueQty: row.IssueQty || 0,
    ReceiveQty: row.ReceiveQty || 0,
    ReturnQty: row.ReturnQty || 0,
    DPRQty: row.DPRQty || 0,
    // Calculate derived fields if not provided by backend, or use backend values
    StockBalance: row.StockBalance ?? (row.ReceiveQty - row.DPRQty - row.ReturnQty),
    ProjectPendingQty: row.ProjectPendingQty ?? (row.SurveyQty - row.DPRQty - row.ReturnQty)
  }));
}

    // --- DETAILED LOGIC (EXISTING) ---
    return reportData.flatMap(product =>
      (product?.activities || [])
        .filter(a => !selectedActivityId || a.ActivityId == selectedActivityId)
        .flatMap(activity =>
          (activity?.subActivities || [])
            .filter(sa => !selectedSubActivityId || sa.SubActivityId == selectedSubActivityId)
            .map(sub => {
              const receive = sub.ReceiveQty || 0;
              const dpr = sub.DPRQty || 0;
              const returnQty = sub.ReturnQty || 0;
              const survey = sub.SurveyQty || 0;

              return {
                ProductCode: product.ProductCode,
                ProductDescription: product.ProductDescription,
                UnitOfMeasureCode: product.UnitOfMeasureCode,
                ActivityCode: activity.ActivityCode,
                SubActivityCode: sub.SubActivityCode,
                SurveyQty: survey,
                IndentQty: sub.IndentQty || 0,
                IssueQty: sub.IssueQty || 0,
                ReceiveQty: receive,
                ReturnQty: returnQty,
                DPRQty: dpr,
                StockBalance: receive - dpr - returnQty,
                ProjectPendingQty: survey - dpr - returnQty
              };
            })
        )
    );
  }, [reportData, selectedActivityId, selectedSubActivityId, isSummaryMode]);

    const handleExportToExcel = () => {
    const dataToExport = filteredReportData.length > 0 ? filteredReportData : flattenedReportData;
    if (!dataToExport || dataToExport.length === 0) { alert("No data to export"); return; }

    const headers = isSummaryMode 
      ? ["Project", "Feeder", "Vendor", "Product Code", "UOM", "Survey Qty", "Indent Qty", "Issue Qty", "Receive Qty", "Return Qty", "DPR Qty", "Stock Balance", "Project Pending Qty"]
      : ["Project", "Feeder", "Vendor", "Product Code", "UOM", "Activity Code", "Sub-Activity Code", "Survey Qty", "Indent Qty", "Issue Qty", "Receive Qty", "Return Qty", "DPR Qty", "Stock Balance", "Project Pending Qty"];

    const csvRows = [headers.join(',')];

    dataToExport.forEach(row => {
      const values = [
        `"${contextInfo.project}"`, `"${contextInfo.feeder}"`, `"${contextInfo.vendor}"`,
        `"${row.ProductCode}${row.ProductDescription ? ' - ' + row.ProductDescription : ''}"`,
        `"${row.UnitOfMeasureCode}"`,
        ...(isSummaryMode ? [] : [`"${row.ActivityCode}"`, `"${row.SubActivityCode}"`]),
        `"${row.SurveyQty}"`, `"${row.IndentQty}"`, `"${row.IssueQty}"`, `"${row.ReceiveQty}"`,
        `"${row.ReturnQty}"`, `"${row.DPRQty}"`, `"${row.StockBalance}"`, `"${row.ProjectPendingQty}"`
      ];
      csvRows.push(values.join(','));
    });

    // --- START: ADDED TOTAL ROW LOGIC ---
    const totalValues = [
      `"TOTAL"`, // Label for the first column
      `""`,      // Feeder
      `""`,      // Vendor
      `""`,      // Product Code
      `""`,      // UOM
      ...(isSummaryMode ? [] : [`""`, `""`]), // Empty Activity/SubActivity if detailed mode
      `"${reportTotals.SurveyQty}"`,
      `"${reportTotals.IndentQty}"`,
      `"${reportTotals.IssueQty}"`,
      `"${reportTotals.ReceiveQty}"`,
      `"${reportTotals.ReturnQty}"`,
      `"${reportTotals.DPRQty}"`,
      `"${reportTotals.StockBalance}"`,
      `"${reportTotals.ProjectPendingQty}"`
    ];
    csvRows.push(totalValues.join(','));
    // --- END: ADDED TOTAL ROW LOGIC ---

    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.setAttribute('hidden', ''); a.setAttribute('href', url);
    const projectNo = projects.find(p => p.ProjectHeaderId == selectedProjectId)?.No || 'Project';
    a.setAttribute('download', `Comparison_${isSummaryMode ? 'Summary' : 'Detail'}_${projectNo}_${new Date().toISOString().slice(0,10)}.csv`);
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  const filteredReportData = useMemo(() => {
    if (!tableSearchTerm) return flattenedReportData;
    const term = tableSearchTerm.toLowerCase();
    return flattenedReportData.filter(row => 
      row.ProductCode?.toLowerCase().includes(term) ||
      row.ActivityCode?.toLowerCase().includes(term) ||
      row.SubActivityCode?.toLowerCase().includes(term)||
       row.ProductDescription?.toLowerCase().includes(term) || // This enables search by Name/Description
      row.UnitOfMeasureCode?.toLowerCase().includes(term) 
    );
  }, [flattenedReportData, tableSearchTerm]);

   const reportTotals = useMemo(() => {
    return filteredReportData.reduce((acc, row) => {
      acc.SurveyQty += row.SurveyQty || 0;
      acc.IndentQty += row.IndentQty || 0;
      acc.IssueQty += row.IssueQty || 0;
      acc.ReceiveQty += row.ReceiveQty || 0;
      acc.ReturnQty += row.ReturnQty || 0;
      acc.DPRQty += row.DPRQty || 0;
      acc.StockBalance += row.StockBalance || 0;
      acc.ProjectPendingQty += row.ProjectPendingQty || 0;
      return acc;
    }, { 
      SurveyQty: 0, IndentQty: 0, IssueQty: 0, ReceiveQty: 0, 
      ReturnQty: 0, DPRQty: 0, StockBalance: 0, ProjectPendingQty: 0 
    });
  }, [filteredReportData]);

  const handleLogout = () => navigate('/login');

  if (loading && projects.length === 0) {
    return (
      <div className="min-h-screen flex bg-[#F1F5F9]">
        <Sidebar onLogout={handleLogout} collapsed={sidebarCollapsed} onToggle={setSidebarCollapsed} theme={{ ...pageTheme, activeBg: pageTheme.accent, activeText: '#ffffff' }} />
        <main className="flex-1 p-4 ml-16 flex items-center justify-center">
          <div className="text-center animate-pulse">
            <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-r from-[#384959] to-[#6A89A7] rounded-full flex items-center justify-center">
              <FaProjectDiagram className="text-3xl text-white" />
            </div>
            <p className="text-[#384959] font-semibold">Loading...</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-[#F1F5F9]">
      <Sidebar onLogout={handleLogout} collapsed={sidebarCollapsed} onToggle={setSidebarCollapsed} theme={{ ...pageTheme, activeBg: pageTheme.accent, activeText: '#ffffff' }} />
      
      {/* Main container: h-screen, flex-col, overflow-hidden */}
      <main className={`h-screen w-full p-4 ml-16 overflow-hidden transition-all duration-300 flex flex-col ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-72'}`}>
        <div className="w-full max-w-[1600px] mx-auto flex flex-col flex-1 min-h-0">
          
          {/* Header - Reduced Margin */}
          <div className="mb-4 relative overflow-hidden rounded-xl bg-gradient-to-r from-[#384959] to-[#6A89A7] p-5 text-white shadow-lg flex-shrink-0">
            <div className="absolute inset-0 bg-black opacity-10"></div>
            <div className="relative z-10 flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold mb-1 flex items-center gap-3">
                  <FaBoxOpen className="text-[#BDDDFC]" /> Comparison Report
                </h1>
                <p className="text-sm opacity-90">Analyze Survey, Indent, Issue, Receive, Return, and DPR quantities</p>
              </div>
              {reportData.length > 0 && (
                <button onClick={handleExportToExcel} className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-all duration-300 shadow-md text-sm font-medium">
                  <FaFileExcel /> <span>Export Excel</span>
                </button>
              )}
            </div>
          </div>

          {/* Filters Card - Reduced Padding/Margin */}
          <div className="bg-white rounded-xl shadow-md border border-[#E2E8F0] p-4 mb-4 flex-shrink-0">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-bold text-[#384959] flex items-center gap-2">
                <FaFilter className="text-[#6A89A7]" /> Filters
              </h3>
              <button onClick={handleResetFilters} className="flex items-center gap-1.5 text-sm font-medium text-[#64748b] hover:text-[#384959] hover:bg-gray-100 px-3 py-1.5 rounded-md transition-all" title="Reset all filters">
                <FaRedo className="text-xs" /> <span>Reset</span>
              </button>
            </div>
            
            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center text-sm">
                <FaExclamationTriangle className="mr-2" /> {error}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 items-end">
              <div>
                <label className="block text-sm font-medium text-[#64748b] mb-1.5">Project <span className='text-red-500'>*</span></label>
                <SearchableDropdown options={projects} value={selectedProjectId} onChange={(val) => { setSelectedProjectId(val); setSelectedFeederId(''); setSelectedActivityId(''); setSelectedSubActivityId(''); }}
                  placeholder="Search Project..." getOptionLabel={(p) => `${p.No} - ${p.Description || ''}`} getOptionValue={(p) => p.ProjectHeaderId} isLoading={loading} />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#64748b] mb-1.5">Feeder</label>
                <SearchableDropdown options={feeders} value={selectedFeederId} onChange={(val) => setSelectedFeederId(val)}
                  placeholder={!selectedProjectId ? "Select Proj first..." : "Search Feeder..."} disabled={!selectedProjectId} isLoading={feedersLoading}
                  getOptionLabel={(f) => `${f.Code} - ${f.Description || ''}`} getOptionValue={(f) => f.FeederId} />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#64748b] mb-1.5">Vendor</label>
                <SearchableDropdown options={vendors} value={selectedVendorCode} onChange={(val) => setSelectedVendorCode(val)}
                  placeholder="Search Vendor..." getOptionLabel={(v) => v.vendor_name || v.name || ''} getOptionValue={(v) => v.vendor_code || v.code || ''} 
                  getOptionSecondaryLabel={(v) => v.vendor_code || v.code || ''} />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#64748b] mb-1.5">Activity</label>
                <SearchableDropdown options={activityOptions} value={selectedActivityId} 
                  onChange={(val) => { setSelectedActivityId(val); setSelectedSubActivityId(''); }}
                  placeholder="All Activities" disabled={reportData.length === 0 || isSummaryMode}
                  getOptionLabel={(a) => a.ActivityCode} getOptionValue={(a) => a.ActivityId} 
                  getOptionSecondaryLabel={(a) => a.ActivityDescription} />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#64748b] mb-1.5">Sub-Activity</label>
                <SearchableDropdown options={subActivityOptions} value={selectedSubActivityId} onChange={(val) => setSelectedSubActivityId(val)}
                  placeholder="All Sub-Activities" disabled={reportData.length === 0 || isSummaryMode}
                  getOptionLabel={(sa) => sa.SubActivityCode} getOptionValue={(sa) => sa.SubActivityId} 
                  getOptionSecondaryLabel={(sa) => sa.SubActivityDescription} />
              </div>

              <div>
                <button onClick={handleGenerateReport} disabled={fetchingData || !selectedProjectId}
                  className="w-full flex items-center justify-center space-x-2 bg-[#6A89A7] hover:bg-[#384959] text-white px-4 py-2.5 rounded-lg transition-all duration-300 shadow-md disabled:opacity-50 disabled:cursor-not-allowed text-sm font-semibold h-[46px]">
                  {fetchingData ? (<><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div><span>Processing...</span></>) 
                  : (<><FaCheckCircle /><span>Show Report</span></>)}
                </button>
              </div>
            </div>
          </div>

          {/* Report Table Card */}
          {reportData.length > 0 && (
            <div className="bg-white rounded-xl shadow-md border border-[#E2E8F0] overflow-hidden flex-1 flex flex-col min-h-0">
              <div className="p-3 border-b border-[#E2E8F0] flex flex-col md:flex-row justify-between items-center bg-[#F9FAFB] gap-3 flex-shrink-0">
                <h3 className="text-lg font-bold text-[#384959] flex items-center gap-2">
                   {isSummaryMode ? <FaFileAlt className="text-[#6A89A7]" /> : <FaBoxOpen className="text-[#6A89A7]" />} 
                   {isSummaryMode ? 'Summary Report' : 'Quantity Details'}
                </h3>
                <div className="flex items-center gap-3 w-full md:w-auto">
                   <button 
                      onClick={handleSummaryReport}
                      disabled={fetchingData || !selectedProjectId}
                      className="flex items-center gap-2 bg-white border border-[#6A89A7] text-[#6A89A7] hover:bg-[#6A89A7] hover:text-white px-3 py-1.5 rounded-lg transition-all duration-300 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium h-[38px] whitespace-nowrap"
                      title="View Product Summary (Excluding Activity/Sub-Activity)"
                    >
                      <FaFileAlt /> <span>Summary Report</span>
                   </button>

                  <div className="relative flex-1 md:flex-none md:w-64">
                    <input type="text" placeholder="Search..." value={tableSearchTerm} onChange={(e) => setTableSearchTerm(e.target.value)}
                      className="w-full pl-9 pr-4 py-2 border border-[#E2E8F0] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#6A89A7]" />
                    <FaSearch className="absolute left-3 top-2.5 text-gray-400 text-sm" />
                  </div>
                  <span className="text-xs text-[#64748b] bg-white px-3 py-1.5 rounded-full border border-[#E2E8F0] shadow-sm font-medium whitespace-nowrap">
                    {filteredReportData.length} Items
                  </span>
                </div>
              </div>
              
              {/* 
                 TABLE WRAPPER:
                 flex-1: Fill available space.
                 min-h-0: Allow shrinking (critical for flex scroll).
                 overflow-auto: Enable scroll.
                 overscroll-contain: Prevent scroll chaining.
              */}
              <div className="flex-1 overflow-auto custom-scrollbar min-h-0 overscroll-contain">
                {/* 
                   TABLE:
                   min-h-full: Ensures table is at least the height of the container (so footer sticks to bottom on short data).
                */}
                <table className="min-w-full min-h-full divide-y divide-[#E2E8F0]">
                  <thead className="bg-[#F9FAFB] sticky top-0 z-20 shadow-sm">
                    <tr>
                      <th className="px-3 py-2.5 text-left text-xs font-bold text-[#64748b] uppercase tracking-wider">Context</th>
                      <th className="px-3 py-2.5 text-left text-xs font-bold text-[#64748b] uppercase tracking-wider">Product Code</th>
                      <th className="px-3 py-2.5 text-left text-xs font-bold text-[#64748b] uppercase tracking-wider">UOM</th>
                      
                      {!isSummaryMode && (
                        <>
                          <th className="px-3 py-2.5 text-left text-xs font-bold text-[#64748b] uppercase tracking-wider">Activity</th>
                          <th className="px-3 py-2.5 text-left text-xs font-bold text-[#64748b] uppercase tracking-wider">Sub Activity</th>
                        </>
                      )}

                      <th className="px-3 py-2.5 text-right text-xs font-bold text-[#64748b] uppercase tracking-wider">Survey</th>
                      <th className="px-3 py-2.5 text-right text-xs font-bold text-[#64748b] uppercase tracking-wider">Indent</th>
                      <th className="px-3 py-2.5 text-right text-xs font-bold text-[#64748b] uppercase tracking-wider">Issue</th>
                      <th className="px-3 py-2.5 text-right text-xs font-bold text-[#64748b] uppercase tracking-wider">Receive</th>
                      <th className="px-3 py-2.5 text-right text-xs font-bold text-[#64748b] uppercase tracking-wider">Return</th>
                      <th className="px-3 py-2.5 text-right text-xs font-bold text-[#64748b] uppercase tracking-wider">DPR</th>
                      <th className="px-3 py-2.5 text-right text-xs font-bold text-[#384959] uppercase tracking-wider bg-gray-100">Stock Bal</th>
                      <th className="px-3 py-2.5 text-right text-xs font-bold text-[#384959] uppercase tracking-wider bg-gray-100">Proj Pending</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-[#E2E8F0]">
                    {filteredReportData.length > 0 ? (
                        filteredReportData.map((row, idx) => (
                        <tr key={idx} className="hover:bg-[#F8FAFC] transition-colors duration-150">
                            <td className="px-3 py-2 text-sm text-[#64748b]">
                                <div className="flex flex-col gap-0.5 whitespace-nowrap">
                                    <span className="font-semibold text-[#384959] text-xs">Proj: {contextInfo.project}</span>
                                    {selectedFeederId && <span className="text-xs">Feed: {contextInfo.feeder}</span>}
                                    {selectedVendorCode && <span className="text-xs">Vend: {contextInfo.vendor}</span>}
                                </div>
                            </td>
                           <td className="px-3 py-2 whitespace-nowrap text-sm">
                            <div className="font-bold text-[#384959]">{row.ProductCode}</div>
                            {row.ProductDescription && (
                              <div className="text-xs text-[#64748b] mt-0.5 max-w-[200px] truncate" title={row.ProductDescription}>
                                {row.ProductDescription}
                              </div>
                            )}
                          </td>
                           <td className="px-3 py-2 whitespace-nowrap text-sm text-[#64748b]">{row.UnitOfMeasureCode}</td>
                           
                           {!isSummaryMode && (
                             <>
                               <td className="px-3 py-2 whitespace-nowrap text-sm text-[#475569]">{row.ActivityCode}</td>
                               <td className="px-3 py-2 whitespace-nowrap text-sm text-[#475569]">{row.SubActivityCode}</td>
                             </>
                           )}
                            
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-[#64748b] text-right">{formatQty(row.SurveyQty)}</td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-[#64748b] text-right">{formatQty(row.IndentQty)}</td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-[#64748b] text-right">{formatQty(row.IssueQty)}</td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-[#64748b] text-right">{formatQty(row.ReceiveQty)}</td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-[#64748b] text-right">{formatQty(row.ReturnQty)}</td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-[#64748b] text-right">{formatQty(row.DPRQty)}</td>

                              <td className={`px-3 py-2 whitespace-nowrap text-sm font-semibold text-right bg-gray-50 ${row.StockBalance < 0 ? 'text-red-600' : 'text-[#384959]'}`}>
                                {formatQty(row.StockBalance)}
                              </td>
                              <td className={`px-3 py-2 whitespace-nowrap text-sm font-semibold text-right bg-gray-50 ${row.ProjectPendingQty < 0 ? 'text-red-600' : 'text-[#384959]'}`}>
                                {formatQty(row.ProjectPendingQty)}
                              </td>
                        </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan={isSummaryMode ? "11" : "13"} className="px-4 py-8 text-center text-sm text-gray-500">No matching products found.</td>
                        </tr>
                    )}
                    
                    {/* SPACER ROW: Keeps last data row visible above sticky footer */}
                    <tr className="h-12">
                       <td colSpan={isSummaryMode ? 11 : 13}></td>
                    </tr>
                  </tbody>
                  
                  {/* STICKY FOOTER */}
                  <tfoot>
                    <tr className="sticky bottom-0 z-20 bg-slate-200 font-bold text-sm text-[#384959] border-t-2 border-slate-400 shadow-[0_-2px_4px_rgba(0,0,0,0.1)]">
                      <td colSpan={isSummaryMode ? 3 : 5} className="px-3 py-2 text-right uppercase tracking-wider">
                        Total
                      </td>
                      {/* SURVEY - Correct position */}
                      <td className="px-3 py-2 text-right">{formatQty(reportTotals.SurveyQty)}</td>
                      {/* INDENT */}
                      <td className="px-3 py-2 text-right">{formatQty(reportTotals.IndentQty)}</td>
                      {/* ISSUE */}
                      <td className="px-3 py-2 text-right">{formatQty(reportTotals.IssueQty)}</td>
                      {/* RECEIVE */}
                      <td className="px-3 py-2 text-right">{formatQty(reportTotals.ReceiveQty)}</td>
                      {/* RETURN */}
                      <td className="px-3 py-2 text-right">{formatQty(reportTotals.ReturnQty)}</td>
                      {/* DPR */}
                      <td className="px-3 py-2 text-right">{formatQty(reportTotals.DPRQty)}</td>
                      {/* STOCK BALANCE */}
                      <td className={`px-3 py-2 text-right bg-slate-300 ${reportTotals.StockBalance < 0 ? 'text-red-600' : ''}`}>
                        {formatQty(reportTotals.StockBalance)}
                      </td>
                      {/* PROJECT PENDING */}
                      <td className={`px-3 py-2 text-right bg-slate-300 ${reportTotals.ProjectPendingQty < 0 ? 'text-red-600' : ''}`}>
                        {formatQty(reportTotals.ProjectPendingQty)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}

          {!fetchingData && reportData.length === 0 && selectedProjectId && (
            <div className="text-center py-16 bg-white rounded-xl shadow-sm border border-[#E2E8F0] flex-shrink-0">
               <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#F1F5F9] mb-4 text-[#A0AEC0]">
                 <FaBoxOpen className="text-2xl" />
               </div>
               <p className="text-[#64748b] font-medium">No data found for the selected criteria.</p>
            </div>
          )}

        </div>
      </main>
    </div>
  );
};

export default ComparisonReport;